import React, {useCallback, useEffect, useState} from 'react';
import IntlMessage, {IntlMessageText} from "../../../../../components/IntlMessage/IntlMessage";
import {Button, Flex, Form, Input, message, Modal, Table} from "antd";
import classes from "../../Applications/application.module.css";
import CertificateService from "../../../../../services/CertificateService";
import MyText from "../../../../../components/UI/Text/MyText";
import {Document, Page} from "react-pdf";
import {API_BASE_URL} from "../../../../../constants/api";
import GreyButton from "../../../../../components/UI/Buttons/GreyButton";
import {useDispatch, useSelector} from "react-redux";
import {getAllCertificates} from "../../../../../store/slices/CertificateSlice";
import BlockLoading from "../../../../../components/LoadingComponents/BlockLoading";
import debounce from "lodash/debounce";
import {signContract} from "../../../../../utils/signContract";
import GreenButton from "../../../../../components/UI/Buttons/GreenButton";
import RedButton from "../../../../../components/UI/Buttons/RedButton";
import {changeCurrentPage} from "../../../../../store/slices/tableController/CertificatesUnsignedListTableController";
import {countUnsignedCertificates} from "../../../../../store/slices/CounterSlice";

const RectorCertificateList = () => {
    const dispatch = useDispatch();
    const {certificates, isLoading, hasMore, error} = useSelector(state => state.certificateList)

    const {currentPage, pageSize} = useSelector(state => state.certificatesUnsignedListTableController);

    const [initialLoading, setInitialLoading] = useState(true);


    const [viewCertificate, setViewCertificate] = useState(false)
    const [openViewCertificate, setOpenViewCertificate] = useState(false)
    const [certificateNumPages, setCertificateNumPages] = useState();
    const [certificatePageNumber, setCertificatePageNumber] = useState(1);

    const [searchForm] = Form.useForm();
    const [searchParams, setSearchParams] = useState({username: '', issueDate: ''});

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const hasSelected = selectedRowKeys.length > 0;

    useEffect(() => {
        const debouncedFetch = debounce(() => {
            dispatch(getAllCertificates({
                page: currentPage,
                limit: pageSize,
                signed: false,
                ...searchParams,
            }));
        }, 500); // 500ms debounce

        debouncedFetch();

        return () => {
            debouncedFetch.cancel();
        };
    }, [searchParams]);


    const fetchData = async (params) => {
        return dispatch(getAllCertificates({
            ...params,
            signed: false,
            ...searchParams,
        }));
    }

    async function handleSignCertificate(ids) {
        message.loading(<IntlMessage id={'changing'}/>, 0);
        await signContract("certificate", ids, null, null)
            .then((r) => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 5);
            })
            .catch((reason) => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            }).finally(() => {
                dispatch(getAllCertificates({
                    page: currentPage,
                    limit: pageSize,
                    signed: false,
                    ...searchParams,
                }));
                dispatch(countUnsignedCertificates());
                setSelectedRowKeys([]);
            });
    }

    const columns = [
        {
            title: <IntlMessage id={'numberWithSymbol'}/>,
            render: (_, record) => (
                <p>{record.notSignedNumber}</p>
            ),
        },
        {
            title: <IntlMessage id={'certificate.code'}/>,
            render: (_, record) => (
                <p>{record.code}</p>
            ),
            width: '20%',
        },
        {
            title: <IntlMessage id={'certificate.user'}/>,
            render: (_, record) => (
                <p>{record.user !== null ? (!!record?.user?.profile ? record.user.profile.fullName : record.user.username) : ""}</p>
            ),
            width: '20%',
        },
        {
            title: <IntlMessage id={'certificate.issueDate'}/>,
            render: (_, record) => (
                <p>{new Date(record.date).toLocaleDateString()}</p>
            ),
            width: '20%',
        },

        {
            title: <IntlMessage id={'certificate.expirationDate'}/>,
            render: (_, record) => (
                <p>{new Date(record.expirationDate).toLocaleDateString()}</p>
            ),
            width: '20%',
        },
        {
            title: <IntlMessage id={'actions'}/>,
            render: (_, record) => (
                <>
                    {
                        <Flex style={{width: "100%"}} align={"center"} justify={"center"} wrap={"wrap"} gap={20}>
                            <p onClick={() => {
                                setViewCertificate(record);
                                setOpenViewCertificate(true);
                            }} className={classes.copyLinkBtn}><IntlMessage id={'view'}/></p>
                            {/*<p onClick={() => downloadDocument(record?.id, record)}*/}
                            {/*   className={classes.copyLinkBtn}><IntlMessage id={'download'}/></p>*/}
                            {
                                record.signature == null &&
                                <p onClick={() => handleSignCertificate([record.id])}
                                   className={classes.copyLinkBtn}><IntlMessage id={'sign'}/></p>
                            }

                        </Flex>
                    }
                </>
            ),
            width: '20%',
        },
    ];

    function onDocumentLoadSuccess({numPages}: { numPages: number }): void {
        setCertificateNumPages(numPages);
    }

    function triggerDownload(blob, filename) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename); // Set the download attribute with a filename
        document.body.appendChild(link);
        link.click();

        // Clean up and revoke the URL after download
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    async function downloadDocument(documentId, certificate) {
        message.loading(<IntlMessage id={'loading'}/>, 0);
        // await CertificateService.getTestResourceById()
        await CertificateService.getResourceById(certificate.code)
            .then((r) => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 5);

                triggerDownload(r.data, certificate?.certificateTemplate?.name + " Certificate.pdf")
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
    }

    const saveCurrentPageSettings = (page, pageSize) => {
        dispatch(changeCurrentPage({page: page, pageSize: pageSize}));
    };

    return (
        <Flex style={{width: "100%"}} align={"flex-start"} justify={"flex-start"} gap={20} vertical>
            <MyText size={"large"} uppercase strong><IntlMessage id={'certificatesForSign'}/></MyText>
            <Flex style={{width: "100%"}} align={"flex-start"} justify={"flex-start"}>
                <Form form={searchForm} layout="inline" style={{marginBottom: 16}}>
                    <Form.Item name="username">
                        <Input
                            placeholder={IntlMessageText.getText({id: "name"})}
                            onChange={e => {
                                saveCurrentPageSettings(1, pageSize)
                                setSearchParams(prev => ({...prev, username: e.target.value}));
                            }}
                        />
                    </Form.Item>
                    <Form.Item name="issueDate">
                        <Input
                            type="date"
                            onChange={e => {
                                saveCurrentPageSettings(1, pageSize)
                                setSearchParams(prev => ({...prev, issueDate: e.target.value}));
                            }}
                        />
                    </Form.Item>
                </Form>
                {
                    hasSelected &&
                    <GreenButton onClick={() => handleSignCertificate(selectedRowKeys)}>
                        <IntlMessage id={'sign'}/>
                    </GreenButton>
                }
            </Flex>
            <BlockLoading isLoading={isLoading}/>
            <MyTable
                isLoading={isLoading}
                dataSource={certificates}
                columns={columns}
                fetchData={fetchData}
                saveCurrentPage={saveCurrentPageSettings}
                initialPage={currentPage}
                initialPageSize={pageSize}
                hasMore={hasMore}
                selectedRowKeys={selectedRowKeys}
                setSelectedRowKeys={setSelectedRowKeys}
            />
            <Modal
                width={800}
                maskClosable={true}
                // title={<IntlMessage id={'contractsPage.details'}/>}
                open={openViewCertificate}
                okText={<IntlMessage id={'save'}/>}
                cancelText={<IntlMessage id={'close'}/>}
                style={{
                    top: 20,
                }}
                onOk={() => {
                    setOpenViewCertificate(false)
                }}
                onCancel={() => {
                    setOpenViewCertificate(false)
                }}
                footer={[
                    <Button key="submit" type="primary" onClick={() => {
                        setOpenViewCertificate(false)
                    }}>
                        OK
                    </Button>
                ]}
            >
                {
                    viewCertificate &&
                    <Flex gap={50}
                          style={{
                              width: "100%",
                              minHeight: "calc(100vh - 300px)",
                              position: "relative",
                              padding: "0px 20px 20px 0px",
                          }}
                          vertical
                          align={"flex-start"}
                          justify={"flex-start"}>
                        <Flex align={"center"} justify={"center"} vertical
                              style={{overflowX: "auto", overflowY: "hidden", width: "100%"}}>
                            <Flex style={{
                                zIndex: 1,
                                padding: "10px 20px",
                                backgroundColor: "white",
                                borderRadius: 10,
                                boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.1)",
                            }} gap={15} justify={"space-between"} align={"center"}>
                                <Button disabled={certificatePageNumber === 1} onClick={() => {
                                    setCertificatePageNumber(certificatePageNumber - 1)
                                }}>
                                    {'<'}
                                </Button>
                                <MyText size={"small"}>
                                    {certificatePageNumber} / {certificateNumPages}
                                </MyText>
                                <Button disabled={certificatePageNumber === certificateNumPages} onClick={() => {
                                    setCertificatePageNumber(certificatePageNumber + 1)
                                }}>
                                    {'>'}
                                </Button>
                            </Flex>
                            <Document file={API_BASE_URL + "/api/certificate/resource/" + viewCertificate?.id}
                                      onLoadSuccess={onDocumentLoadSuccess}
                                      loading={<IntlMessage id={'documentIsLoading'}/>}
                            >
                                <Page pageNumber={certificatePageNumber} width={700}/>
                            </Document>
                        </Flex>
                        <Flex align={"flex-start"} justify={"center"} gap={20} style={{width: "100%"}}>
                            <GreyButton onClick={() => downloadDocument(viewCertificate?.id, viewCertificate)}>
                                <IntlMessage id={'downloadContract'}/>
                            </GreyButton>
                        </Flex>
                    </Flex>
                }
            </Modal>
        </Flex>
    );
};

const MyTable = ({
                     isLoading,
                     dataSource,
                     columns,
                     fetchData,
                     saveCurrentPage,
                     initialPage = 1,
                     initialPageSize = 100,
                     hasMore = true,
                     expandable,
                     selectedRowKeys,
                     setSelectedRowKeys
                 }) => {
    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };
    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const [currentPage, setCurrentPage] = useState(initialPage);
    const [currentPageSize, setCurrentPageSize] = useState(initialPageSize);
    const [hasNextPage, setHasNextPage] = useState(hasMore);

    useEffect(() => {
        setHasNextPage(hasMore);
    }, [hasMore]);

    useEffect(() => {
        setCurrentPage(initialPage);
        setCurrentPageSize(initialPageSize);
    }, [initialPage, initialPageSize]);

    // Handle pagination change by updating the current page and page size.
    // Then, fetch data using the provided fetchData function.
    const handlePaginationChange = useCallback(
        (page, pageSize) => {
            setCurrentPage(page);
            setCurrentPageSize(pageSize);
            fetchData({page, limit: pageSize}).then((result) => {
                const hasMore = result.payload.hasMore;
                setHasNextPage(hasMore);
            });
            saveCurrentPage(page, pageSize);
        },
        [fetchData],
    );

    return (
        <Table
            rowSelection={rowSelection}
            bordered
            columns={columns}
            dataSource={dataSource}
            pagination={{
                current: currentPage,
                total: hasNextPage
                    ? currentPage * currentPageSize + 1
                    : currentPage * currentPageSize,
                defaultPageSize: initialPageSize,
                showSizeChanger: true,
                pageSize: currentPageSize,
                pageSizeOptions: ['5', '10', '20', '30', '50', '100'],
                onChange: handlePaginationChange,
                onShowSizeChange: handlePaginationChange,
            }}
            rowKey={record => record.id}
            loading={isLoading}
            style={{
                width: "100%",
            }}
            scroll={{x: 800}}
        />
    )
}

export default RectorCertificateList;